import React, { useState, useEffect, useCallback } from "react";

import { useMsal } from "@azure/msal-react";

import { FileText } from "lucide-react";

import { callApi } from "../api/api";

import ResponsiveTableCards from "../components/ResponsiveTableCards";
import ScrollableTable from "../components/ScrollableTable";
import { resolveAdlsPath } from "../utils/resolveAdlsPath";

export default function PolicySearch() {
  const { instance, accounts } = useMsal();

  const FILESYSTEM =
    process.env.REACT_APP_ADLS_ARCHIEVE_POLICYSEARCH_FILESYSTEM || "cfpfs";

  /* =========================
     SEARCH
  ========================= */

  const [policyNo, setPolicyNo] = useState("");

  const [results, setResults] = useState([]);

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState("");

  /* =========================
     PREVIEW
  ========================= */

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const [previewUrl, setPreviewUrl] = useState(null);

  const [previewContentType, setPreviewContentType] = useState("");

  const [previewError, setPreviewError] = useState(null);

  const [searchController, setSearchController] = useState(null);

  const [previewLoading, setPreviewLoading] = useState(false);

  const [downloadLoading, setDownloadLoading] = useState(false);

  const [isTiff, setIsTiff] = useState(false);
  const [isNarrow, setIsNarrow] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsNarrow(window.innerWidth <= 600);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  /* =========================
     SEARCH
  ========================= */

  const runSearch = async () => {
    /*
    Prevent duplicate searches
  */

    if (loading) {
      return;
    }

    /*
    Cancel previous request
  */

    if (searchController) {
      searchController.abort();
    }

    const controller = new AbortController();

    setSearchController(controller);

    try {
      setError("");

      if (!policyNo.trim()) {
        setError("Enter policy number");

        return;
      }

      setLoading(true);

      setResults([]);

      const res = await callApi(
        instance,
        accounts,
        "GET",
        "/api/admin/policy-search",
        null,
        {
          signal: controller.signal,

          params: {
            policyno: policyNo.trim(),
          },
        },
      );

      /*
      Ignore cancelled requests
    */

      if (controller.signal.aborted) {
        return;
      }

      setResults(res?.data?.data || []);
    } catch (err) {
      /*
      Ignore cancellation
    */

      if (err?.name === "AbortError" || err?.message === "Request cancelled") {
        return;
      }

      console.error(err);

      setError(err?.response?.data?.message || err.message || "Search failed");
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  };

  /* =========================
     DOWNLOAD
  ========================= */

  const handleDownload = useCallback(async (path) => {
    /*
    Prevent rapid spam downloads
  */

    if (downloadLoading) {
      return;
    }

    const controller = new AbortController();

    try {
      setDownloadLoading(true);

      const cleaned = path?.trim();

      if (!cleaned) {
        throw new Error("Invalid file path");
      }

      const { filesystem, path: filePath } = resolveAdlsPath(
        cleaned,
        FILESYSTEM,
      );

      const baseUrl =
        process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

      /*
      TOKEN
    */

      const tokenResponse = await instance.acquireTokenSilent({
        account: instance.getActiveAccount(),
      });

      /*
      DOWNLOAD
    */

      const response = await fetch(
        `${baseUrl}/api/adls/download` +
          `?filesystem=${encodeURIComponent(filesystem)}` +
          `&path=${encodeURIComponent(filePath)}`,
        {
          method: "GET",

          signal: controller.signal,

          headers: {
            Authorization: `Bearer ${tokenResponse.accessToken}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Download failed");
      }

      const blob = await response.blob();

      /*
      Empty response protection
    */

      if (!blob || blob.size === 0) {
        throw new Error("Empty download response");
      }

      /*
      FILE NAME
    */

      const fileName = filePath.split("/").pop() || "download";

      /*
      DOWNLOAD FILE
    */

      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = blobUrl;

      link.download = fileName;

      document.body.appendChild(link);

      link.click();

      link.remove();

      /*
      Cleanup memory
    */

      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 60000);
    } catch (err) {
      if (err?.name === "AbortError" || err?.message === "Request cancelled") {
        return;
      }

      console.error("Download failed", err);

      setError(err.message || "Download failed");
    } finally {
      controller.abort();

      setDownloadLoading(false);
    }
  }, [instance, FILESYSTEM, downloadLoading]);

  /* =========================
     PREVIEW
  ========================= */

  const getExtensionFromPath = useCallback((path = "") => {
    return path.split("?")[0].split(".").pop()?.toLowerCase();
  }, []);

  const handlePreview = useCallback(async (path) => {
    /*
    Prevent preview spam
  */

    if (previewLoading) {
      return;
    }

    const controller = new AbortController();

    try {
      setPreviewLoading(true);

      setIsPreviewOpen(true);

      setPreviewError(null);

      /*
      Cleanup previous blob URL
    */

      if (previewUrl && previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }

      setPreviewUrl(null);
      setPreviewContentType("");

      const cleaned = path?.trim();

      if (!cleaned) {
        throw new Error("Invalid file path");
      }

      const { filesystem, path: filePath } = resolveAdlsPath(
        cleaned,
        FILESYSTEM,
      );

      const ext = getExtensionFromPath(filePath);

      const isTiffFile = ext === "tif" || ext === "tiff";

      const baseUrl =
        process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

      const tokenResponse = await instance.acquireTokenSilent({
        account: instance.getActiveAccount(),
      });

      /*
      TIFF -> BLOB API
    */

      const endpoint = isTiffFile
        ? "/api/adls/preview-blob"
        : "/api/adls/preview";

      const res = await fetch(
        `${baseUrl}${endpoint}` +
          `?filesystem=${encodeURIComponent(filesystem)}` +
          `&path=${encodeURIComponent(filePath)}`,
        {
          method: "GET",

          signal: controller.signal,

          headers: {
            Authorization: `Bearer ${tokenResponse.accessToken}`,
          },
        },
      );

      if (!res.ok) {
        throw new Error(`Preview request failed (${res.status})`);
      }

      const blob = await res.blob();

      /*
      Archive .txt files are stored as a generic type. Label them as
      text so the existing preview frame can show the contents.
    */
      let previewBlob = blob;

      if (ext === "txt" && !String(blob.type || "").startsWith("text/")) {
        previewBlob = new Blob([blob], {
          type: "text/plain;charset=utf-8",
        });
      }

      setPreviewContentType(previewBlob.type);

      /*
      Empty blob protection
    */

      if (!previewBlob || previewBlob.size === 0) {
        throw new Error("Empty preview response");
      }

      /*
      STANDARD FILES
    */
      if (
        previewBlob.type.startsWith("image/") ||
        previewBlob.type === "application/pdf" ||
        previewBlob.type === "text/html" ||
        previewBlob.type.startsWith("text/")
      ) {
        const blobUrl = URL.createObjectURL(previewBlob);

        setPreviewUrl(blobUrl);

        return;
      }

      /*
      TIFF FILES
    */

      setPreviewError("Preview not supported");
    } catch (err) {
      /*
      Ignore cancellation
    */

      if (err?.name === "AbortError" || err?.message === "Request cancelled") {
        return;
      }

      console.error(err);

      setPreviewError(err.message || "Preview failed");
    } finally {
      controller.abort();

      setPreviewLoading(false);
    }
  }, [instance, FILESYSTEM, previewLoading, previewUrl, getExtensionFromPath]);

  /* =========================
     TIFF
  ========================= */

  const closePreview = () => {
    /*
    Cleanup blob URLs
  */

    if (previewUrl && previewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(previewUrl);
    }

    setIsPreviewOpen(false);

    setPreviewUrl(null);
    setPreviewContentType("");

    setPreviewError(null);
  };

  /* =========================
     RESPONSIVE CARD MAPPERS
  ========================= */

  const mapPolicyCard = useCallback(
    (r) => ({
      id: `policy-${r.PolicyNumber}-${r.itemname}`,
      icon: <FileText size={16} />,
      tone: "info",
      title: r.PolicyNumber || "—",
      subtitle: r.Name || "—",
      meta: [
        {
          label: "Effective",
          value: r.StartDate
            ? new Date(r.StartDate).toLocaleDateString()
            : "—",
        },
        {
          label: "Expiry",
          value: r.EndDate ? new Date(r.EndDate).toLocaleDateString() : "—",
        },
        { label: "Document", value: r.itemname || "—" },
      ],
      actions: [
        {
          label: "Preview",
          icon: <FileText size={14} />,
          onClick: () => handlePreview(r.adls_path),
        },
      ],
    }),
    [handlePreview],
  );

  const mapPolicyDrawer = useCallback(
    (r) => ({
      title: r.PolicyNumber || "—",
      fields: [
        { label: "Insured Name", value: r.Name || "—" },
        {
          label: "Effective Date",
          value: r.StartDate
            ? new Date(r.StartDate).toLocaleDateString()
            : "—",
        },
        {
          label: "Expiry Date",
          value: r.EndDate ? new Date(r.EndDate).toLocaleDateString() : "—",
        },
      ],
      attachments: [
        {
          label: r.itemname || "Document",
          onPreview: () => handlePreview(r.adls_path),
          onDownload: () => handleDownload(r.adls_path),
        },
      ],
    }),
    [handlePreview, handleDownload],
  );

  const renderTable = useCallback(
    () => (
      <ScrollableTable style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>POLICY</th>

              <th style={styles.th}>INSURED</th>

              <th style={styles.th}>EFFECTIVE</th>

              <th style={styles.th}>EXPIRY</th>

              <th style={styles.th}>DOCUMENT</th>

              <th style={styles.th}>ACTIONS</th>
            </tr>
          </thead>

          <tbody>
            {results.map((r, index) => (
              <tr key={index} style={styles.row}>
                <td style={styles.td}>{r.PolicyNumber}</td>

                <td style={styles.td}>{r.Name}</td>

                <td style={styles.td}>
                  {r.StartDate
                    ? new Date(r.StartDate).toLocaleDateString()
                    : "—"}
                </td>

                <td style={styles.td}>
                  {r.EndDate
                    ? new Date(r.EndDate).toLocaleDateString()
                    : "—"}
                </td>

                <td style={styles.td}>
                  <span style={styles.fileBadge}>{r.itemname}</span>
                </td>

                <td style={styles.td}>
                  <div style={styles.actionRow}>
                    <button
                      style={styles.previewButton}
                      onClick={() => handlePreview(r.adls_path)}
                    >
                      Preview
                    </button>

                    <button
                      style={styles.downloadButton}
                      onClick={() => handleDownload(r.adls_path)}
                    >
                      Download
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ScrollableTable>
    ),
    [results, handlePreview, handleDownload],
  );

  /* =========================
     UI
  ========================= */

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>Archival Policy Search</h2>

            <p style={styles.sub}>Search archived policy documents</p>
          </div>
        </div>

        {/* SEARCH */}

        <div style={styles.searchCard}>
          <div
            style={{
              ...styles.searchRow,
              ...(isNarrow ? styles.searchRowStack : {}),
            }}
          >
            <div
              style={{
                ...styles.field,
                ...(isNarrow ? styles.fieldStack : {}),
              }}
            >
              <label style={styles.label}>Policy Number</label>

              <input
                style={{
                  ...styles.input,
                  ...(isNarrow ? styles.inputStack : {}),
                }}
                placeholder="Enter policy number"
                value={policyNo}
                onChange={(e) => setPolicyNo(e.target.value)}
              />
            </div>

            <button
              style={{
                ...styles.button,
                ...(isNarrow ? styles.buttonStack : {}),
              }}
              onClick={runSearch}
              disabled={loading}
            >
              {loading ? "Searching..." : "Search"}
            </button>
          </div>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {/* RESULTS */}

        <div style={{ marginTop: "24px" }}>
          <ResponsiveTableCards
            breakpoint={1024}
            data={results}
            loading={loading}
            emptyTitle="No Records Found"
            emptySub="Try a different policy number."
            listTitle="Policy Search Results"
            listSubtitle="Tap a record to preview or download"
            cardMapper={mapPolicyCard}
            drawerMapper={mapPolicyDrawer}
            renderTable={renderTable}
          />
        </div>
      </div>

      {/* PREVIEW */}

      {isPreviewOpen && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <span>Preview</span>

              <button onClick={closePreview} style={styles.closeButton}>
                ✕
              </button>
            </div>

            <div style={styles.modalBody} className="scrollable">
              {previewError && <div>{previewError}</div>}

              {previewUrl &&
                (previewContentType.startsWith("image/") ? (
                  <img
                    src={previewUrl}
                    alt="Preview"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      display: "block",
                      margin: "0 auto",
                    }}
                  />
                ) : (
                  <iframe
                    title="Preview"
                    src={previewUrl}
                    style={{
                      width: "100%",
                      height: "100%",
                      border: "none",
                    }}
                  />
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
   
    background: "#f8fafc",
    minHeight: "80vh",
  },

  card: {
    background: "#ffffff",
    borderRadius: "16px",
    padding: "24px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
  },

  header: {
    marginBottom: "20px",
  },

  title: {
    margin: 0,
    fontSize: "28px",
    fontWeight: "700",
    color: "#0f172a",
  },

  sub: {
    color: "#64748b",
    marginTop: "6px",
    fontSize: "14px",
  },

  searchCard: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "14px",
    padding: "clamp(12px, 4vw, 18px)",
    width: "100%",
    boxSizing: "border-box",
  },

  searchRow: {
    display: "flex",
    gap: "clamp(12px, 3vw, 16px)",
    flexWrap: "wrap",
    alignItems: "flex-end",
    width: "100%",
    boxSizing: "border-box",
  },
  searchRowStack: {
    flexDirection: "column",
    alignItems: "stretch",
    gap: "12px",
  },

  field: {
    display: "flex",
    flexDirection: "column",
    minWidth: "260px",
    flex: 1,
    width: "100%",
    boxSizing: "border-box",
  },
  fieldStack: {
    minWidth: "auto",
    width: "100%",
  },

  label: {
    fontSize: "12px",
    fontWeight: "600",
    marginBottom: "6px",
    color: "#475569",
    textTransform: "uppercase",
  },

  input: {
    padding: "12px 14px",
    border: "1px solid #cbd5e1",
    borderRadius: "10px",
    fontSize: "14px",
    background: "#fff",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  },
  inputStack: {
    maxWidth: "100%",
  },

  button: {
    background: "#0f172a",
    color: "#fff",
    border: "none",
    padding: "12px 20px",
    borderRadius: "10px",
    cursor: "pointer",
    fontWeight: "600",
    height: "46px",
    minWidth: "140px",
    width: "auto",
    boxSizing: "border-box",
  },
  buttonStack: {
    width: "100%",
  },

  error: {
    marginTop: "20px",
    background: "#fee2e2",
    color: "#991b1b",
    padding: "12px",
    borderRadius: "10px",
  },

  tableWrap: {
    overflowX: "auto",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "1000px",
  },

  th: {
    textAlign: "center",
    padding: "14px",
    background: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
    fontSize: "11px",
    color: "#64748b",
    fontWeight: "600",
  },

  td: {
    padding: "16px 14px",
    borderBottom: "1px solid #f1f5f9",
    verticalAlign: "top",
    fontSize: "13px",
    textAlign: "center",
  },

  row: {
    transition: "background 0.2s ease",
  },

  fileBadge: {
    background: "#f1f5f9",
    padding: "6px 10px",
    borderRadius: "8px",
    fontSize: "12px",
    display: "inline-block",
  },

  actionRow: {
    display: "flex",
    gap: "10px",
  },

  previewButton: {
    background: "#0f766e",
    color: "#fff",
    border: "none",
    padding: "8px 12px",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "500",
  },

  downloadButton: {
    background: "#475569",
    color: "#fff",
    border: "none",
    padding: "8px 12px",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "500",
  },

  empty: {
    textAlign: "center",
    padding: "40px",
    color: "#64748b",
  },

  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },

  modal: {
    width: "90%",
    height: "90%",
    background: "#fff",
    borderRadius: "16px",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },

  modalHeader: {
    padding: "16px",
    borderBottom: "1px solid #e2e8f0",
    display: "flex",
    justifyContent: "space-between",
    fontWeight: "600",
  },

  modalBody: {
    flex: 1,
    overflow: "auto",
    padding: "16px",
  },

  closeButton: {
    border: "none",
    background: "transparent",
    fontSize: "18px",
    cursor: "pointer",
  },
};
